import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { ApiClient, Nummernkreis } from "@kundeninfo/api-client";
import { Feld, useBeforeUnload, useFormularZustand } from "@kundeninfo/ui";
import { Formular } from "@kundeninfo/ui/me-design";
import { ENTITY_LABEL, KEINE_ZAHL, Meldung, alsZahl, eingabeUnlesbar, formatKonfliktZeit,
         meldungAus } from "./gemeinsam.js";
import "../Verwaltung.css";
import { KiKnopf } from "../../components/meHuellen.js";

/**
 * Der Verwaltungsbereich NUMMERNKREISE.
 *
 * Herausgelöst aus views/Verwaltung.tsx (Etappe 2, Aufgabe 3) — rein strukturell, CSS und
 * Verhalten unverändert (Block `vw__`, views/Verwaltung.css).
 *
 * **Ohne zweite Ebene** (Entscheid Aufgabe 3, so schon im Aufgabenzuschnitt vorgesehen):
 * Die Kreise entstehen nicht und vergehen nicht — es gibt je Unternehmen genau vier, fest
 * angelegt mit dem Unternehmen. Der Bereich ist damit eine TABELLE mit festem Umfang, deren
 * Sinn gerade der Überblick über alle Kreise nebeneinander ist („welches Unternehmen
 * nummeriert wie?"). Es gibt weder Anlegen noch Löschen, für die eine Liste da wäre, und
 * ein Detail hinter einem Klick zeigte genau die zwei Felder, die schon in der Zeile stehen.
 */

/** Nur die drei Wege dieses Bereichs — so lässt er sich im Test mit drei vi.fn zeichnen. */
export type NummernkreiseClient = Pick<ApiClient,
  "listNumberRanges" | "updateNumberRangePattern" | "setNumberRangeStart">;

function alsNummernkreis(wert: unknown): Nummernkreis | null {
  if (typeof wert !== "object" || wert === null) return null;
  const n = wert as Partial<Nummernkreis>;
  if (typeof n.id !== "string" || typeof n.updatedAt !== "string") return null;
  return wert as Nummernkreis;
}

export function NummernkreiseBereich(
  { client }: { client: NummernkreiseClient },
): ReactElement {
  const [zeilen, setZeilen] = useState<Nummernkreis[] | null>(null);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [entwuerfe, setEntwuerfe] = useState<Record<string, string>>({});
  /** Der getippte Startwert je Kreis — getrennt vom Muster-Entwurf, weil beide Felder
   *  getrennt gespeichert werden (siehe `NummernkreisZeile`). */
  const [startEntwuerfe, setStartEntwuerfe] = useState<Record<string, string>>({});

  const listNumberRanges = client.listNumberRanges;

  useEffect(() => {
    let abgebrochen = false;
    listNumberRanges()
      .then((antwort) => { if (!abgebrochen) setZeilen(antwort.items); })
      .catch((fehler: unknown) => {
        if (!abgebrochen) {
          setLadeFehler(
            meldungAus(fehler, "Die Nummernkreise konnten nicht geladen werden."));
        }
      });
    return () => { abgebrochen = true; };
  }, [listNumberRanges]);

  function musterVon(z: Nummernkreis): string {
    return entwuerfe[z.id] ?? z.pattern;
  }

  function startwertVon(z: Nummernkreis): string {
    return startEntwuerfe[z.id] ?? String(z.startValue);
  }

  /** Wie im Benutzerbereich: irgendein Muster weicht vom gespeicherten ab (2a-Kleinpunkt). */
  const etwasGeaendert = (zeilen ?? []).some((z) => musterVon(z) !== z.pattern);
  useBeforeUnload(etwasGeaendert);

  function vergiss(id: string): void {
    setEntwuerfe((e) => { const rest = { ...e }; delete rest[id]; return rest; });
  }

  function vergissStart(id: string): void {
    setStartEntwuerfe((e) => { const rest = { ...e }; delete rest[id]; return rest; });
  }

  /** Den Stand einer Zeile ersetzen — nach einem Speichern (die Antwort trägt den neuen
   *  Zeitstempel) und nach „Neu laden" (der fremde Stand). */
  function ersetze(neu: Nummernkreis): void {
    setZeilen((alt) => (alt === null ? alt : alt.map((x) => (x.id === neu.id ? neu : x))));
  }

  if (ladeFehler !== null) return <Meldung text={ladeFehler} />;
  if (zeilen === null) return <p className="vw__hinweis">Lädt …</p>;

  return (
    <ul className="vw__liste">
      {zeilen.map((z) => (
        <NummernkreisZeile key={z.id} client={client} kreis={z}
          muster={musterVon(z)} startwert={startwertVon(z)}
          onMuster={(w) => setEntwuerfe((e) => ({ ...e, [z.id]: w }))}
          onStartwert={(w) => setStartEntwuerfe((e) => ({ ...e, [z.id]: w }))}
          onMusterGespeichert={(neu) => { ersetze(neu); vergiss(z.id); }}
          onStartGespeichert={(neu) => { ersetze(neu); vergissStart(z.id); }}
          onFremderStand={(fremd) => { ersetze(fremd); vergiss(z.id); vergissStart(z.id); }} />
      ))}
    </ul>
  );
}

type ZeilenProps = {
  client: NummernkreiseClient;
  kreis: Nummernkreis;
  muster: string;
  startwert: string;
  onMuster: (wert: string) => void;
  onStartwert: (wert: string) => void;
  onMusterGespeichert: (neu: Nummernkreis) => void;
  onStartGespeichert: (neu: Nummernkreis) => void;
  /** „Neu laden" nach einer 409: der Stand des Servers ersetzt Zeile UND Entwürfe. */
  onFremderStand: (fremd: Nummernkreis) => void;
};

/**
 * Eine Zeile — ein Kreis mit seinen ZWEI Schreibwegen (Muster, Startwert) in EINEM
 * Formular (ME-Etappe 3, Aufgabe 6). Eigene Komponente, weil jede Zeile ihren eigenen
 * Formularzustand braucht (`useFormularZustand` je Kreis): Speichert der eine Kreis,
 * bleibt der nächste bedienbar.
 *
 * Beide Wege laufen durch denselben Zustand: `weg` sagt, welcher gerade geschickt wird.
 * Nicht zusammen in einem Aufruf: Der Vertrag trägt genau eine der beiden Gestalten je
 * Rumpf (sonst 400), und beides zusammen wären ohnehin zwei Schreibvorgänge — der zweite
 * liefe gegen den Zeitstempel, den der erste gerade verstellt hat, und der Aufrufer bekäme
 * einen Konflikt mit sich selbst.
 *
 * Eine 409 (Nummernkreise tragen keine `version`; verglichen wird der Zeitstempel des
 * zuletzt bekannten Standes, Entscheidung C) hält die Eingaben als „fremder Stand" fest;
 * „Neu laden" übernimmt den Stand, den der Server mit der 409 schickt — sonst die Liste.
 * Das ersetzt den früheren Kasten „Fremden Stand übernehmen".
 */
function NummernkreisZeile(p: ZeilenProps): ReactElement {
  const z = p.kreis;
  const weg = useRef<"muster" | "start">("muster");
  const fremd = useRef<Nummernkreis | null>(null);

  const formular = useFormularZustand<Nummernkreis>({
    speichern: async () => {
      try {
        if (weg.current === "muster") {
          // Der Zeitstempel kommt aus der Zeile — und die Zeile wird durch die ANTWORT
          // ersetzt, sonst schlüge das zweite Speichern mit 409 fehl, obwohl niemand
          // dazwischenkam.
          const neu = await p.client.updateNumberRangePattern(z.id, z.updatedAt, p.muster);
          p.onMusterGespeichert(neu);
          return neu;
        }
        const zahl = alsZahl(p.startwert);
        if (zahl === KEINE_ZAHL || zahl === null) {
          throw eingabeUnlesbar([{ field: "startValue", message: "Bitte eine Zahl eintragen." }]);
        }
        const neu = await p.client.setNumberRangeStart(z.id, z.updatedAt, zahl);
        p.onStartGespeichert(neu);
        return neu;
      } catch (fehler) {
        if (fehler instanceof ApiError && fehler.status === 409) {
          fremd.current = alsNummernkreis(fehler.conflict?.current);
        }
        throw fehler;
      }
    },
    neuLaden: async () => {
      let stand = fremd.current;
      fremd.current = null;
      if (stand === null) {
        const antwort = await p.client.listNumberRanges();
        stand = antwort.items.find((x) => x.id === z.id) ?? null;
      }
      if (stand !== null) p.onFremderStand(stand);
    },
    // NUMMERN_BEREITS_VERGEBEN kommt am Feld `startValue` — genau dort soll die Sperre
    // auch stehen, nicht als Sammelmeldung an der Zeile.
    bekannteFelder: z.vergeben ? ["pattern"] : ["pattern", "startValue"],
    beschriftungen: { pattern: "Muster", startValue: "Startwert" },
  });
  const bereit = formular.zustand === "bereit";
  const kreisName = `${z.companyShortCode} ${ENTITY_LABEL[z.entity]}`;

  function speichern(welcher: "muster" | "start"): void {
    weg.current = welcher;
    void formular.aktion();
  }

  return (
    <li className="vw__eintrag">
      <div className="vw__eintrag-kopf">
        <span className="vw__eintrag-name">
          {z.companyShortCode} · {ENTITY_LABEL[z.entity]}
        </span>
      </div>
      {formular.meldung !== null && <Meldung text={formular.meldung} />}
      <Formular beschriftung="oben" {...formular.formularProps}
        hinweis={formular.zustand === "fremder-stand" ? (
          <span>
            Jemand hat das Muster inzwischen geändert
            {formular.fremderStand === null ? "" : ` (${formular.fremderStand.changedBy}, `
              + `${formatKonfliktZeit(formular.fremderStand.changedAt)})`}. Deine Eingabe
            ist nicht verloren — „Neu laden" übernimmt den aktuellen Stand.
          </span>
        ) : undefined}>
        <Feld id={`vw-nr-${z.id}`} label="Muster" bearbeitbar wert={p.muster}
          onChange={(w) => { p.onMuster(w); formular.feldFehlerRaeumen("pattern"); }}
          {...(formular.feldFehler["pattern"] !== undefined
            ? { fehler: formular.feldFehler["pattern"] } : {})} />
        {/*
          Der Zähler wird ANGEZEIGT, nicht bearbeitet — er gehört dem Nummernvergabe-
          Weg (`allocateNumber`), und ein Eingabefeld hier lüde dazu ein, ihn
          zurückzudrehen und damit bereits vergebene Nummern ein zweites Mal zu
          erzeugen. Deshalb reiner Text, kein deaktiviertes Feld (siehe Feld.tsx).
        */}
        <span className="vw__eintrag-zusatz">Zähler: {z.counter}</span>
        <div className="vw__eintrag-aktionen">
          <KiKnopf art="primaer" deaktiviert={p.muster === z.pattern || !bereit} name={`Muster für ${kreisName} speichern`} onClick={() => speichern("muster")}>
            Speichern
          </KiKnopf>
        </div>

        {/*
          Der Startwert: der Zählerstand VOR der ersten Nummer (bei 500 trägt der erste
          Satz die 501) — für die Übernahme aus einem Altsystem. Änderbar NUR, solange
          noch keine Nummer vergeben ist: Danach entstünden Lücken oder Doubletten in
          einer bereits laufenden Nummernfolge (GoBD-Nähe). Ist er gesperrt, steht er
          als TEXT da und nicht als abgeblendetes Feld — mit dem Grund daneben, denn ein
          graues Feld ohne Erklärung sieht aus wie ein Fehler.
        */}
        {z.vergeben ? (
          <>
            <Feld id={`vw-nr-start-${z.id}`} label="Startwert" bearbeitbar={false}
              wert={String(z.startValue)} onChange={() => undefined} />
            <span className="vw__eintrag-zusatz">
              Gesperrt: Aus diesem Kreis wurden bereits Nummern vergeben.
            </span>
          </>
        ) : (
          <>
            <Feld id={`vw-nr-start-${z.id}`} label="Startwert" bearbeitbar
              wert={p.startwert}
              onChange={(w) => { p.onStartwert(w); formular.feldFehlerRaeumen("startValue"); }}
              {...(formular.feldFehler["startValue"] !== undefined
                ? { fehler: formular.feldFehler["startValue"] } : {})} />
            <div className="vw__eintrag-aktionen">
              <KiKnopf deaktiviert={p.startwert === String(z.startValue) || !bereit} name={`Startwert für ${kreisName} speichern`} onClick={() => speichern("start")}>
                Startwert speichern
              </KiKnopf>
            </div>
          </>
        )}
      </Formular>
    </li>
  );
}
